import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Asset, AssetInput, Level } from '../types'
import { updateAsset } from '../services/assetService'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { getLevelsByProjectId } from '../services/levelService'

export interface AssetLevelFormProps {
  asset: Asset
  beforeSubmit?: (values: AssetInput) => void
  afterSubmit?: (asset: Asset) => void
  onCancel?: () => void
}

export default function AssetLevelForm({
  asset,
  beforeSubmit,
  afterSubmit,
  onCancel
}: AssetLevelFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The levels state.
   */
  const [levels, setLevels] = useState<Level[]>([])

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The initial form values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId
  }

  /**
   * Load the levels.
   */
  async function loadLevels(): Promise<void> {
    try {
      setLoading(true)

      if (asset.projectId !== null) {
        setLevels(
          (await getLevelsByProjectId(asset.projectId)).sort(
            (a, b) => (a.number ?? 0) - (b.number ?? 0)
          )
        )
      } else {
        setLevels([])
      }
    } catch (_) {
      // Do nothing.
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the "submit" event.
   */
  async function handleSubmit(values: AssetInput, { setSubmitting }: FormikHelpers<AssetInput>) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values)

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('updateAssetSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset)
    } catch (_) {
      dispatch(createErrorNotification(t('updateAssetError')))
      setSubmitting(false)
    }
  }

  /**
   * Load the levels when the component mounts.
   */
  useEffect(() => {
    loadLevels()
  }, [asset?.projectId])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, setFieldTouched, isSubmitting, handleChange, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="level-select-label">{t('level')}</InputLabel>
                <Select
                  id="level-select"
                  labelId="level-select-label"
                  name="level"
                  label={t('level')}
                  value={values.level}
                  disabled={loading || isSubmitting}
                  autoFocus
                  onChange={handleChange}
                >
                  {levels.map((level) => (
                    <MenuItem key={level.id} value={level.id}>
                      {level.name}
                    </MenuItem>
                  ))}
                </Select>
                {values.level !== null && (
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      disabled={isSubmitting}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setFieldValue('level', null)
                        setFieldTouched('level', true)
                      }}
                    >
                      {t('clearSelection')}
                    </Button>
                  </Stack>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
